import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Grid, Divider,
  Card, CardContent, CircularProgress, Alert, FormHelperText, Switch, FormGroup,
  FormControlLabel, styled
} from '@mui/material';
import { ArrowBack, Save, Cancel } from '@mui/icons-material';
import useAdd from '../../api/useAdd';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { useDateFormat, DATE_FORMATS } from '../../context/DateFormatContext';
import { formatShamsiDate, formatGregorianDate, formatHijriDate } from '../../utils/dateUtils';
import LocalizedDatePicker from '../../components/common/LocalizedDatePicker';
import { RTLTextField, RTLFormControl, RTLButton } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

const DocumentCreate = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { t, i18n } = useTranslation(['documents', 'common']);
  const { isRTL, getRTLProps } = useRTL();
  const { dateFormat } = useDateFormat();
  const token = auth.user?.token;

  const formatDate = (dateString, forInput = false) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    if (forInput) {
      return date.toISOString().split('T')[0];
    }
    
    return dateFormat === DATE_FORMATS.SHAMSI ? formatShamsiDate(date, isRTL) :
      dateFormat === DATE_FORMATS.QAMARI ? formatHijriDate(date, isRTL) :
      formatGregorianDate(date, isRTL);
  };

  const [formData, setFormData] = useState({
    title: '', 
    reference_number: '', 
    description: '', 
    type: '', 
    priority: 'عادي',
    department_id: '', 
    directorate_id: '', 
    deputy_ministry_id: '', 
    source_type: 'INTERNAL',
    external_source_name: '', 
    document_date: formatDate(new Date(), true), 
    number_of_pages: '',
    archive_registration_date: '', 
    archive_number: '', 
    is_confidential: false
  });

  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const { handleAdd, loading } = useAdd('documents', token, null, t('documents:create_success'));

  const documentTypes = ['مکتوب', 'استعلام', 'پیشنهاد', 'گزارش'].map(v => ({ 
    value: v, 
    label: t(`documents:${v}`, v) 
  }));
  
  const priorities = ['عاجل', 'اطمینانیه', 'ابلاغیه', 'محرم', 'عادي'].map(v => ({ 
    value: v, 
    label: t(`documents:${v}`, v) 
  }));
  
  const sourceTypes = ['INTERNAL', 'EXTERNAL', 'PROVINCIAL'].map(v => ({ 
    value: v, 
    label: t(`documents:source_${v.toLowerCase()}`, v) 
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title) errors.title = t('validation.required');
    if (!formData.type) errors.type = t('validation.required');
    if (!formData.priority) errors.priority = t('validation.required');
    if (!formData.document_date) errors.document_date = t('validation.required');
    if (formData.source_type !== 'INTERNAL' && !formData.external_source_name) {
      errors.external_source_name = t('validation.required_for_external');
    }
    setFormErrors(errors);
    return !Object.keys(errors).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return setError(t('validation.fix_errors'));

    try {
      await handleAdd(formData);
      navigate('/documents');
    } catch (err) {
      setError(err.message || t('common:error_occurred'));
    }
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 4, 
        flexDirection: isRTL ? 'row-reverse' : 'row' 
      }}>
        <RTLButton 
          component={RouterLink} 
          to="/documents" 
          startIcon={!isRTL && <ArrowBack />} 
          endIcon={isRTL && <ArrowBack />} 
          {...getRTLProps('button')}
        >
          {t('common:back')}
        </RTLButton>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            flexGrow: 1, 
            fontWeight: 500, 
            color: 'primary.main', 
            textAlign: isRTL ? 'right' : 'left' 
          }}
        >
          {t('documents:create_title')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 1 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3, 
                fontWeight: 500, 
                borderBottom: '1px solid', 
                borderColor: 'divider', 
                pb: 1 
              }}
            >
              {t('documents:basic_info')}
            </Typography>
            
            <Grid container spacing={3}>
              {[
                { 
                  name: 'title', 
                  label: t('documents:title'), 
                  required: true, 
                  xs: 12, 
                  sm: 6,
                  md: 6
                },
                { 
                  name: 'type', 
                  label: t('documents:type'), 
                  select: true, 
                  options: documentTypes, 
                  required: true, 
                  xs: 12, 
                  sm: 6,
                  md: 6
                },
                { 
                  name: 'priority', 
                  label: t('documents:priority'), 
                  select: true, 
                  options: priorities, 
                  required: true, 
                  xs: 12, 
                  sm: 6,
                  md: 6
                },
                { 
                  name: 'reference_number', 
                  label: t('documents:reference_number'), 
                  xs: 12, 
                  sm: 6,
                  md: 6
                },
                { 
                  name: 'document_date', 
                  label: t('documents:document_date'), 
                  type: 'date', 
                  helperText: dateFormat !== DATE_FORMATS.GREGORIAN ? 
                    t('documents:displayed_as') + ': ' + formatDate(formData.document_date) : '', 
                  xs: 12, 
                  sm: 6,
                  md: 6
                },
                { 
                  name: 'number_of_pages', 
                  label: t('documents:number_of_pages'), 
                  type: 'number', 
                  xs: 12, 
                  sm: 6,
                  md: 6
                },
                { 
                  name: 'source_type', 
                  label: t('documents:doc_source_type'), 
                  select: true, 
                  options: sourceTypes, 
                  xs: 12, 
                  sm: 6,
                  md: 6
                },
                ...(formData.source_type !== 'INTERNAL' ? [
                  { 
                  name: 'external_source_name', 
                  label: t('documents:doc_external_source'), 
                  required: true, 
                  xs: 12, 
                  sm: 6,
                  md: 6
                  },
                  { 
                    name: 'archive_registration_date', 
                    label: t('documents:archive_registration_date'), 
                    type: 'date', 
                    helperText: dateFormat !== DATE_FORMATS.GREGORIAN ? 
                      t('documents:displayed_as') + ': ' + formatDate(formData.archive_registration_date) : '', 
                    xs: 12, 
                    sm: 6,
                    md: 6
                  },
                  { 
                    name: 'archive_number', 
                    label: t('documents:archive_number'), 
                    xs: 12, 
                    sm: 6,
                    md: 6
                  }
                ] : []),
                { 
                  name: 'description', 
                  label: t('documents:description'), 
                  multiline: true, 
                  rows: 4, 
                  xs: 12, 
                  sm: 12,
                  md: 12
                }
              ].map((field, i) => field.select ? (
                <Grid item xs={field.xs} sm={field.sm} md={field.md ?? field.sm} key={i}>
                  <RTLFormControl 
                    fullWidth 
                    required={field.required} 
                    {...getRTLProps('select')} 
                    error={!!formErrors[field.name]} 
                    size="small"
                  >
                    <InputLabel>
                      {field.label}
                    </InputLabel>
                    <Select 
                      name={field.name} 
                      value={formData[field.name]} 
                      onChange={handleInputChange} 
                      label={field.label}
                      size="small"
                    >
                      {field.options.map(opt => (
                        <MenuItem 
                          key={opt.value} 
                          value={opt.value}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors[field.name] && (
                      <FormHelperText>
                        {formErrors[field.name]}
                      </FormHelperText>
                    )}
                  </RTLFormControl>
                </Grid>
              ) : (
                <Grid item xs={field.xs} sm={field.sm} md={field.md ?? field.sm} key={i}>
                  {field.type === 'date' ? (
                    <LocalizedDatePicker
                      label={field.label}
                      value={formData[field.name]}
                      onChange={(e) => handleInputChange(e)}
                      name={field.name}
                      {...getRTLProps('datepicker')}
                      inputProps={{
                        required: field.required,
                        error: !!formErrors[field.name],
                        helperText: formErrors[field.name] || field.helperText,
                      }}
                    />
                  ) : (
                    <RTLTextField
                      fullWidth
                      required={field.required}
                      size="small"
                      variant="outlined"
                      label={field.label}
                      name={field.name}
                      type={field.type}
                      multiline={field.multiline}
                      rows={field.rows}
                      value={formData[field.name]}
                      onChange={handleInputChange}
                      error={!!formErrors[field.name]}
                      helperText={formErrors[field.name] || field.helperText}
                      {...getRTLProps('textfield')}
                      InputLabelProps={{ 
                        shrink: Boolean(formData[field.name]),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: formErrors[field.name] ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover fieldset': {
                            borderColor: formErrors[field.name] ? 'error.main' : 'primary.main',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: formErrors[field.name] ? 'error.main' : 'primary.main',
                            borderWidth: 1,
                          },
                        },
                      }}
                    />
                  )}
                </Grid>
              ))}
              
              
            </Grid>

            <Divider sx={{ my: 4 }} />
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: isRTL ? 'flex-start' : 'flex-end', 
              gap: 2 
            }}>
              <RTLButton 
                variant="outlined" 
                component={RouterLink} 
                to="/documents" 
                startIcon={!isRTL && <Cancel />} 
                endIcon={isRTL && <Cancel />} 
                {...getRTLProps('button')}
              >
                {t('common:cancel')}
              </RTLButton>
              <RTLButton 
                variant="contained" 
                type="submit" 
                disabled={loading} 
                startIcon={!isRTL && <Save />} 
                endIcon={isRTL && <Save />} 
                {...getRTLProps('button')}
              >
                {loading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                {t('common:save')}
              </RTLButton>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default DocumentCreate;